import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, SpeakerHigh, SpeakerSlash, GearSix } from "@phosphor-icons/react";

const KIND_ICON = {
  matrimony: { emoji: "💗", color: "#D95A41" },
  job: { emoji: "💼", color: "#2C4A3B" },
  marketplace: { emoji: "🛍", color: "#D49A36" },
};

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const diff = Math.max(1, (Date.now() - t) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Web Audio: a subtle two-note "ding" — no external files, ~0.5s total.
let _audioCtx = null;
function playChime(volume = 0.12) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = volume;
    master.connect(ctx.destination);
    // Two soft sine tones — E5 then G5
    [[659.25, 0.0], [783.99, 0.14]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + delay);
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(1, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.35);
      osc.connect(gain).connect(master);
      osc.start(now + delay);
      osc.stop(now + delay + 0.4);
    });
  } catch { /* ignore audio errors (user hasn't interacted yet, etc.) */ }
}

const CHANNELS = [
  { key: "matrimony", label: "Matrimony", color: "#D95A41" },
  { key: "job", label: "Jobs", color: "#2C4A3B" },
  { key: "marketplace", label: "Marketplace", color: "#D49A36" },
];

function loadPrefs() {
  try {
    const raw = localStorage.getItem("anv_notif_prefs");
    if (raw) return JSON.parse(raw);
  } catch (err) { console.error("parse notif prefs failed", err); }
  return { matrimony: true, job: true, marketplace: true };
}

// -------- Web Push helpers --------
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function ensurePushSubscription() {
  if (typeof window === "undefined") return { state: "unsupported" };
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return { state: "unsupported" };
  const reg = await navigator.serviceWorker.ready;
  if (Notification.permission === "denied") return { state: "denied", reg };
  if (Notification.permission === "default") {
    const p = await Notification.requestPermission();
    if (p !== "granted") return { state: p, reg };
  }
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try { await api.post("/push/subscribe", { subscription: existing.toJSON() }); } catch (err) { console.error("push subscribe failed", err); }
    return { state: "subscribed", subscription: existing };
  }
  const { data } = await api.get("/push/vapid-public-key");
  if (!data.configured) return { state: "server-off" };
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.key),
  });
  try { await api.post("/push/subscribe", { subscription: sub.toJSON() }); } catch (err) { console.error("push subscribe failed", err); }
  return { state: "subscribed", subscription: sub };
}

async function removePushSubscription() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch (err) { console.error("push unsubscribe failed", err); }
      await sub.unsubscribe();
    }
  } catch (err) { console.error("removePushSubscription failed", err); }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [muted, setMuted] = useState(() => localStorage.getItem("anv_notif_muted") === "1");
  const [prefs, setPrefs] = useState(loadPrefs);
  const [showPrefs, setShowPrefs] = useState(false);
  const [pushState, setPushState] = useState("idle"); // idle|subscribed|denied|unsupported|server-off
  const wrapRef = useRef(null);
  const prevIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  // Try to enroll in Web Push once on mount (permission is only requested on first user-interaction fallback below)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) { setPushState("unsupported"); return; }
    // Only auto-subscribe if already granted; otherwise leave it to the toggle button
    if (Notification.permission === "granted") {
      ensurePushSubscription().then((r) => setPushState(r.state || "idle")).catch(() => {});
    } else if (Notification.permission === "denied") {
      setPushState("denied");
    }
  }, []);

  const togglePush = async () => {
    if (pushState === "subscribed") {
      await removePushSubscription();
      setPushState("idle");
      return;
    }
    const r = await ensurePushSubscription();
    setPushState(r.state || "idle");
  };

  const load = async () => {
    try {
      const { data: fresh } = await api.get("/notifications");
      const newIds = new Set(fresh.items.map((i) => i.id));
      if (!firstLoadRef.current && prevIdsRef.current) {
        const arrived = fresh.items.filter((i) => !prevIdsRef.current.has(i.id) && !i.read);
        // Only chime if global mute is off AND at least one new item is on an enabled channel
        const chimeable = arrived.filter((i) => prefs[i.kind] !== false);
        if (chimeable.length > 0 && !muted) playChime();
      }
      prevIdsRef.current = newIds;
      firstLoadRef.current = false;
      setData(fresh);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [muted, prefs]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setShowPrefs(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setData((d) => ({ items: d.items.map((i) => ({ ...i, read: true })), unread: 0 }));
    } catch (err) { console.error("mark all read failed", err); }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("anv_notif_muted", next ? "1" : "0");
      if (!next) playChime(0.08);
      return next;
    });
  };

  const togglePref = (key) => {
    setPrefs((p) => {
      const next = { ...p, [key]: !p[key] };
      localStorage.setItem("anv_notif_prefs", JSON.stringify(next));
      return next;
    });
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full flex items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3] relative"
        style={{ transition: "background-color 180ms ease-out" }}
        aria-label="Notifications"
      >
        <Bell size={16} weight={data.unread ? "fill" : "regular"} className="text-[#2C4A3B]" />
        {data.unread > 0 && (
          <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-[#F9F6F0] flex items-center justify-center" style={{ background: "#D95A41" }}>
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl bg-[#F9F6F0] border border-[#E5D9CC] shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#E5D9CC]">
            <div>
              <div className="font-heading text-lg text-[#1C1B1A]">Notifications</div>
              <div className="text-xs text-[#5C564D]">{data.unread > 0 ? `${data.unread} unread` : "You're all caught up"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="btn-mute"
                onClick={toggleMute}
                className="p-1.5 rounded-full hover:bg-[#F2EBE5]"
                style={{ transition: "background-color 180ms ease-out" }}
                aria-label={muted ? "Un-mute chime" : "Mute chime"}
                title={muted ? "Chime is off — click to turn on" : "Chime is on — click to mute"}
              >
                {muted ? <SpeakerSlash size={14} className="text-[#5C564D]" /> : <SpeakerHigh size={14} weight="fill" className="text-[#2C4A3B]" />}
              </button>
              <button
                data-testid="btn-notif-prefs"
                onClick={() => setShowPrefs((v) => !v)}
                className="p-1.5 rounded-full hover:bg-[#F2EBE5]"
                style={{ transition: "background-color 180ms ease-out" }}
                aria-label="Notification preferences"
                title="Pick which channels chime"
              >
                <GearSix size={14} className="text-[#5C564D]" />
              </button>
              {data.unread > 0 && (
                <button data-testid="mark-all-read" onClick={markAllRead} className="text-xs font-semibold text-[#2C4A3B] hover:underline flex items-center gap-1">
                  <Check size={12} weight="bold" /> Mark all
                </button>
              )}
            </div>
          </div>
          {showPrefs && (
            <div className="px-4 py-3 border-b border-[#E5D9CC] bg-[#F2EBE5]" data-testid="notif-prefs-panel">
              <div className="text-xs font-semibold text-[#1C1B1A] mb-2">Chime for these channels</div>
              <div className="space-y-2">
                {CHANNELS.map((c) => (
                  <label key={c.key} data-testid={`pref-${c.key}`} className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-[#1C1B1A] flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                      {c.label}
                    </span>
                    <input
                      type="checkbox"
                      checked={prefs[c.key] !== false}
                      onChange={() => togglePref(c.key)}
                      className="w-4 h-4 accent-[#D95A41]"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-[#E5D9CC]">
                <div className="text-xs font-semibold text-[#1C1B1A] mb-2">Browser push notifications</div>
                <button
                  data-testid="btn-push-toggle"
                  onClick={togglePush}
                  disabled={pushState === "unsupported" || pushState === "server-off" || pushState === "denied"}
                  className={pushState === "subscribed" ? "samaaj-btn-outline text-xs w-full" : "samaaj-btn-primary text-xs w-full"}
                >
                  {pushState === "subscribed" && "Turn off browser push"}
                  {pushState === "idle" && "Turn on browser push"}
                  {pushState === "denied" && "Blocked (change in browser settings)"}
                  {pushState === "unsupported" && "Not supported on this device"}
                  {pushState === "server-off" && "Server push not configured"}
                </button>
                <div className="text-[10px] text-[#5C564D] mt-1.5">Get chimed on your phone or laptop even when the tab is closed.</div>
              </div>
              <div className="text-xs text-[#5C564D] mt-2">You'll still see every notification in this list — this just controls the chime.</div>
            </div>
          )}
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#5C564D]">No notifications yet.</div>
            ) : data.items.map((n) => {
              const meta = KIND_ICON[n.kind] || { emoji: "🔔", color: "#5C564D" };
              return (
                <Link
                  key={n.id}
                  to={n.link || "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`notif-${n.id}`}
                  className="flex gap-3 px-4 py-3 border-b border-[#E5D9CC]/70 hover:bg-[#F2EBE5]"
                  style={{ transition: "background-color 180ms ease-out", background: n.read ? "transparent" : "#F2EBE5" }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0" style={{ background: meta.color, color: "#F9F6F0" }}>{meta.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1C1B1A] line-clamp-2">{n.title}</div>
                    {n.body && <div className="text-xs text-[#5C564D] mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-[#5C564D] mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full mt-1.5" style={{ background: "#D95A41" }} />}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
